import { useMemo } from 'react';
import type { Appointment, ListAppointmentsParams } from '@/api/mcp-appointments';
import { useListAppointmentsQuery } from '@/api/mcp-appointments';
import { parseAppointmentContext } from '@/components/appointments/extensions/extension-utils';

/**
 * Build a map of context id -> appointment for a given context source.
 * When `ids` are provided, fetches only matching appointments via API context filters.
 */
export function useAppointmentsByContextId({
  source,
  idKey,
  ids,
  enabled = true,
}: {
  source: string;
  idKey: string;
  ids?: string[];
  enabled?: boolean;
}) {
  const sortedIds = useMemo(() => {
    if (!ids || ids.length === 0) return undefined;
    return [...ids].map(String).sort();
  }, [ids]);

  const listParams = useMemo((): ListAppointmentsParams | undefined => {
    if (!sortedIds || sortedIds.length === 0) {
      return undefined;
    }

    return {
      contextSource: source,
      contextField: idKey,
      contextValues: sortedIds
    };
  }, [sortedIds, source, idKey]);

  const queryEnabled = enabled && !!sortedIds && sortedIds.length > 0;

  const { data: appointments, isLoading, error, refetch } = useListAppointmentsQuery(
    listParams,
    queryEnabled
  );

  const byId = useMemo(() => {
    const map = new Map<string, Appointment>();
    if (!appointments || !Array.isArray(appointments)) return map;

    for (const appt of appointments) {
      const parsed = parseAppointmentContext(appt as Appointment & { context?: { source: string; data: Record<string, unknown> } | null });
      if (!parsed || parsed.source !== source) continue;
      const rawId = parsed.data?.[idKey];
      if (rawId == null) continue;
      map.set(String(rawId), appt);
    }

    return map;
  }, [appointments, source, idKey]);

  return { byId, appointments, isLoading, error, refetch } as const;
}
